import { Link, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Compass,
  Menu,
  X,
  Mail,
  LogIn,
  Lock,
  LogOut,
  Settings as SettingsIcon,
  Home,
} from 'react-feather';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import { useAuth } from '../hooks/useAuth';
import { useWriteback } from '../contexts/WritebackContext';
import NotificationBell from './NotificationBell.tsx';
import TrialCountdownChip from './remember/TrialCountdownChip';
import { trialCountdownOf } from '../services/trialService';
import { PaywallSource } from '../services/monetizationEvents';
import logoImg from '../../images/logo-no-tagline.png';
import iconImg from '../../images/icon_transparent.png';
import { version as appVersion } from '../../package.json';
import { useState, useEffect, useRef } from 'react';

// Posts icon (custom SVG matching design reference)
const PostsIcon = ({ size = 18, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5Z" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M7 8h10M7 12h10M7 16h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

// Contacts / "my ppl" icon (custom SVG)
const MyPeopleIcon = ({ size = 18, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M4 5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v15l-3-2-3 2-3-2-3 2-3-2V5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
  </svg>
);

// Insights sparkle (PPL-265) — the four-point spark that marks every
// write-back surface, matching the app's SparkleGlyph.
const InsightsIcon = ({ size = 18, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" fill="currentColor" />
    <path d="M18.5 15l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2Z" fill="currentColor" opacity="0.75" />
  </svg>
);

// History clock icon
const HistoryIcon = ({ size = 18, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

// Circles icon (hexagon)
const CirclesIcon = ({ size = 18, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M12 2l8.5 5v10L12 22l-8.5-5V7L12 2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
  </svg>
);

// Rooms icon (two people)
const RoomsIcon = ({ size = 18, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
    <circle cx="8" cy="8.5" r="3.2" stroke="currentColor" strokeWidth="1.8"/>
    <circle cx="16" cy="8.5" r="3.2" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M3 19c0-2.5 2.2-4.2 5-4.2M21 19c0-2.5-2.2-4.2-5-4.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const Navigation = ({ user, collapsed = false, onToggleCollapsed = null }) => {
  const controlledCollapsed = collapsed !== undefined;
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const isCollapsed = controlledCollapsed ? collapsed : internalCollapsed;
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [mobileUserMenuOpen, setMobileUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const unreadMessages = useUnreadMessages(user?.uid);
  // PPL-265 — same subscription the /insights page reads: one number, every
  // surface. Badge suppressed entirely at zero, never a "0".
  const { pendingTotal: writebackPending } = useWriteback();
  const mobileMenuRef = useRef(null);
  const mobileUserRef = useRef(null);

  // Determine active tab from current route
  const getActiveTab = () => {
    const p = location.pathname;
    if (p === '/f8chat') return 'chat';
    if (p === '/messages' || p.startsWith('/messages/')) return 'messaging';
    if (p === '/chat-history') return 'history';
    if (p === '/circles' || p.startsWith('/circles/')) return 'circles';
    if (p === '/find-ppl' || p === '/meet-ppl' || p === '/connect-with') return 'people';
    if (p === '/contacts' || p.startsWith('/contacts/')) return 'contacts';
    // PPL-399 — insights is a top-level destination.
    if (p === '/insights') return 'insights';
    if (p === '/rooms' || p.startsWith('/rooms/')) return 'rooms';
    if (p === '/mediation' || p.startsWith('/mediation/')) return 'rooms';
    if (p === '/premium') return 'premium';
    if (p === '/settings') return 'settings';
    if (p.startsWith('/user/')) return 'profile';
    // PPL-395 — the own-profile read view and its edit form are one section.
    if (p === '/profile' || p === '/profile/edit') return 'profile';
    // Default: posts (/, /new, /discussed, /talk/*)
    return 'posts';
  };

  const activeTab = getActiveTab();

  const toggleCollapsed = () => {
    if (controlledCollapsed && onToggleCollapsed) {
      onToggleCollapsed();
    } else {
      setInternalCollapsed(c => !c);
    }
  };

  // Focus trap for mobile menu
  useEffect(() => {
    if (!showMobileMenu) return;
    const menu = mobileMenuRef.current;
    if (!menu) return;

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = menu.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [showMobileMenu]);

  // Close mobile user menu on click outside or Escape
  useEffect(() => {
    if (!mobileUserMenuOpen) return;
    const onDoc = (e) => {
      if (mobileUserRef.current && !mobileUserRef.current.contains(e.target)) setMobileUserMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMobileUserMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [mobileUserMenuOpen]);

  const userName = user?.displayName || user?.firstName || '';
  const userInitial = (userName || user?.email || '?')[0].toUpperCase();
  const showPremium = !!user && user.selectedPlan !== 'premium';
  // PPL-358 — the rail row is the persistent, discoverable countdown surface:
  // quiet from day 1, never in the way, and honest that the trial IS premium.
  const trialCountdown = trialCountdownOf(user);

  const goNewChat = () => {
    navigate('/f8chat', { state: { newChat: Date.now() } });
    setShowMobileMenu(false);
  };

  // Drawer sections mirroring the app drawer (PplDrawer.tsx):
  // insights (top, no eyebrow) / talk / explore / your world / app,
  // lowercase labels + eyebrows.
  const sections = [
    {
      // PPL-399 — insights is a first-class destination: top of the nav,
      // directly under home, above talk. It carries the review-queue badge
      // (it is the queue's door) and stays present at zero as the archive
      // of what f8 knows.
      eyebrow: null,
      items: [
        { id: 'insights', label: 'insights', icon: InsightsIcon, path: '/insights', auth: true, badge: writebackPending },
      ],
    },
    {
      eyebrow: 'talk',
      items: [
        { id: 'messaging', label: 'messaging', icon: Mail, path: '/messages', auth: true, badge: unreadMessages },
        { id: 'rooms', label: 'rooms', icon: RoomsIcon, path: '/rooms', auth: true },
        { id: 'history', label: 'chat history', icon: HistoryIcon, path: '/chat-history', auth: true },
      ],
    },
    {
      eyebrow: 'explore',
      items: [
        { id: 'posts', label: 'posts', icon: PostsIcon, path: '/posts' },
        { id: 'circles', label: 'circles', icon: CirclesIcon, path: '/circles' },
        { id: 'people', label: 'discover', icon: Compass, path: '/find-ppl' },
      ],
    },
    {
      eyebrow: 'your world',
      items: [
        { id: 'contacts', label: 'my ppl', icon: MyPeopleIcon, path: '/contacts', auth: true },
      ],
    },
    {
      eyebrow: 'app',
      items: [
        { id: 'settings', label: 'settings', icon: SettingsIcon, path: '/settings', auth: true },
        ...(showPremium
          ? [trialCountdown.isTrialing
            ? {
              id: 'premium',
              label: `on premium · ${trialCountdown.daysLeft} day${trialCountdown.daysLeft === 1 ? '' : 's'} left`,
              premium: true,
              path: `/premium?source=${PaywallSource.TRIAL_COUNTDOWN}`,
            }
            : { id: 'premium', label: 'upgrade to premium', premium: true, path: '/premium' }]
          : []),
        {
          id: 'privacy', label: 'privacy policy', icon: Lock,
          action: () => window.open('https://pplkpr.ai/privacy.html', '_blank', 'noopener'),
        },
        user
          ? { id: 'signout', label: 'sign out', icon: LogOut, action: () => logout() }
          : { id: 'login', label: 'log in', icon: LogIn, path: '/login' },
      ],
    },
  ];

  const handleItemClick = (item) => {
    setShowMobileMenu(false);
    if (item.action) {
      item.action();
    } else {
      navigate(item.path);
    }
  };

  const renderSections = () => (
    <>
      {sections.map((section) => {
        const items = section.items.filter(item => !item.auth || user);
        if (items.length === 0) return null;
        return (
          <div key={section.eyebrow ?? 'top'} className={`rail-section${section.eyebrow ? '' : ' rail-section--top'}`}>
            {section.eyebrow && <div className="rail-divider" />}
            {section.eyebrow && <div className="rail-eyebrow">{section.eyebrow}</div>}
            <nav className="rail-nav" role="navigation" aria-label={section.eyebrow ?? 'insights'}>
              {items.map(item => {
                const isActive = item.id === activeTab;
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    className={`rail-nav-item${isActive ? ' active' : ''}`}
                    style={item.nested ? { marginLeft: 26 } : undefined}
                    onClick={() => handleItemClick(item)}
                  >
                    {item.premium ? (
                      <span className="rail-premium-icon">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3Z" fill="#fff"/>
                        </svg>
                      </span>
                    ) : (
                      <span className="nav-icon-wrapper">
                        <Icon size={18} className="nav-icon" />
                        {item.badge > 0 && (
                          <span className="nav-badge">{item.badge > 99 ? '99+' : item.badge}</span>
                        )}
                      </span>
                    )}
                    <span className="nav-label">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        );
      })}
    </>
  );

  const renderProfileHeader = () => user && (
    <button className="rail-profile" onClick={() => { setShowMobileMenu(false); navigate('/profile'); }}>
      {user.photoURL ? (
        <img src={user.photoURL} alt="" className="rail-user-avatar" />
      ) : (
        <div className="rail-user-avatar-placeholder">{userInitial}</div>
      )}
      <span className="rail-profile-info">
        {user.email && <span className="rail-profile-email">{user.email}</span>}
        <span className="rail-profile-link">view profile</span>
      </span>
    </button>
  );

  // PPL-394 — Carl's call: the rail item is "home", not "new chat". It lands on
  // the desk (the /f8chat initial state), which is what "new chat" always did.
  const renderNewChatButton = () => (
    <div className="rail-new-chat-wrap">
      <button className="rail-new-chat-btn" onClick={goNewChat} aria-label="home">
        <Home size={16} />
        <span className="btn-label">home</span>
      </button>
    </div>
  );

  const renderFooter = () => (
    <div className="rail-footer">
      <div className="rail-footer-line">web version {appVersion}</div>
      {user?.email && <div className="rail-footer-line">logged in as {user.email}</div>}
    </div>
  );

  return (
    <>
      {/* ─── Desktop left rail ─── */}
      <aside className={`left-rail${isCollapsed ? ' collapsed' : ''}`}>
        {/* Logo */}
        <Link to="/f8chat" className="rail-logo">
          <img src={isCollapsed ? iconImg : logoImg} alt="pplkpr" className={isCollapsed ? 'rail-logo-collapsed' : 'rail-logo-icon'} />
        </Link>

        {/* Profile header */}
        {renderProfileHeader()}

        {/* Primary action */}
        {renderNewChatButton()}

        {/* Grouped nav sections + footer */}
        <div className="rail-body">
          {renderSections()}
          <div className="rail-spacer" />
          {renderFooter()}
        </div>
      </aside>

      {/* ─── Mobile header ─── */}
      <header className="mobile-header">
        <div className="mobile-header-inner">
          <div className="mobile-header-left">
            <button className="mobile-menu-btn" onClick={() => setShowMobileMenu(true)} aria-label="Open menu">
              <Menu size={20} />
            </button>
            <Link to="/f8chat" className="mobile-header-logo-link" aria-label="Home">
              <img src={logoImg} alt="pplkpr" className="mobile-logo" />
            </Link>
          </div>
          <div className="mobile-header-actions">
            {user ? (
              <>
                {/* PPL-358 — a trialist IS on premium; countdown, not cold upsell. */}
                {user.selectedPlan !== 'premium' && (trialCountdown.isTrialing ? (
                  <TrialCountdownChip countdown={trialCountdown} />
                ) : (
                  <Link to="/premium" className="top-bar-premium-cta" aria-label="Go Premium">
                    <span className="f8-sparkle-nav">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                        <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3Z" fill="#fff"/>
                      </svg>
                    </span>
                    <span>go premium</span>
                  </Link>
                ))}
                <Link to="/messages" className="mobile-header-icon-btn" aria-label="Messages">
                  <span className="nav-icon-wrapper">
                    <Mail size={18} />
                    {unreadMessages > 0 && (
                      <span className="nav-badge">{unreadMessages > 99 ? '99+' : unreadMessages}</span>
                    )}
                  </span>
                </Link>
                <NotificationBell />
                <div className="mobile-user-pod-wrap" ref={mobileUserRef}>
                  <button
                    className="mobile-header-avatar-btn"
                    onClick={() => setMobileUserMenuOpen(o => !o)}
                    aria-label="User menu"
                  >
                    {user.photoURL ? (
                      <img src={user.photoURL} alt="" className="top-bar-avatar" />
                    ) : (
                      <div className="top-bar-avatar-placeholder">{userInitial}</div>
                    )}
                  </button>
                  {/* Mobile user popover — reuses rail-user-popover styles */}
                  <div className={`mobile-user-popover${mobileUserMenuOpen ? ' open' : ''}`}>
                    <div className="rail-user-popover__header">
                      {user.photoURL ? (
                        <img src={user.photoURL} alt="" className="rail-user-avatar" />
                      ) : (
                        <div className="rail-user-avatar-placeholder">{userInitial}</div>
                      )}
                      <div className="rail-user-popover__header-info">
                        <div className="rail-user-popover__name">{userName || 'Guest'}</div>
                        <div className="rail-user-popover__email">{user.email || ''}</div>
                      </div>
                    </div>
                    <button className="rail-user-popover__item" onClick={() => { setMobileUserMenuOpen(false); navigate('/profile'); }}>
                      <span className="rail-user-popover__icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.7"/><path d="M4 21a8 8 0 0 1 16 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
                      </span>
                      <span>View profile</span>
                    </button>
                    <button className="rail-user-popover__item" onClick={() => { setMobileUserMenuOpen(false); navigate('/settings'); }}>
                      <span className="rail-user-popover__icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
                      </span>
                      <span>Settings</span>
                    </button>
                    <div className="rail-user-popover__divider" />
                    <button className="rail-user-popover__item destructive" onClick={() => { setMobileUserMenuOpen(false); logout(); }}>
                      <span className="rail-user-popover__icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </span>
                      <span>Log out</span>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <Link to="/login" className="mobile-header-login" aria-label="Login">
                <LogIn size={18} />
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* ─── Mobile menu overlay ─── */}
      <div
        className={`mobile-menu-overlay${showMobileMenu ? ' open' : ''}`}
        onClick={() => setShowMobileMenu(false)}
        aria-hidden={!showMobileMenu}
      >
        <div
          className={`mobile-menu${showMobileMenu ? ' open' : ''}`}
          onClick={(e) => e.stopPropagation()}
          ref={mobileMenuRef}
        >
          <div className="mobile-menu-header">
            <img src={logoImg} alt="pplkpr" />
            <button className="mobile-menu-close" onClick={() => setShowMobileMenu(false)} aria-label="Close menu">
              <X size={20} />
            </button>
          </div>
          <div className="mobile-nav">
            {renderProfileHeader()}
            {renderNewChatButton()}
            {renderSections()}
            {renderFooter()}
          </div>
        </div>
      </div>
    </>
  );
};

Navigation.propTypes = {
  user: PropTypes.object
};

export default Navigation;
